import React from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import { ArrowRight, BookOpen, Cpu, Network, ShieldCheck, Volume2, Workflow } from 'lucide-react';

const videos = [
  {
    category: 'Semiconductor inspection',
    title: 'Qualify critical components',
    description: 'Precision inspection and controlled analysis help expose defects before components enter mission-critical systems.',
    src: '/media/vikrant-salar/semiconductor-inspection.mp4',
    poster: '/media/vikrant-salar/semiconductor-inspection.webp',
  },
  {
    category: 'Hardware evidence assurance',
    title: 'Turn telemetry into decisions',
    description: 'Board-level testing converts technical evidence into clear reliability and procurement decisions.',
    src: '/media/vikrant-salar/hardware-evidence-assurance.mp4',
    poster: '/media/vikrant-salar/hardware-evidence-assurance.webp',
  },
  {
    category: 'AI infrastructure reliability',
    title: 'Validate hardware integrity',
    description: 'Power, cooling, connectivity, and system evidence support resilient next-generation computing infrastructure.',
    src: '/media/vikrant-salar/ai-infrastructure-reliability.mp4',
    poster: '/media/vikrant-salar/ai-infrastructure-reliability.webp',
  },
  {
    category: 'Supply-chain provenance',
    title: 'Trace risk across every tier',
    description: 'Component identity and global dependency pathways become visible before hidden concentration becomes disruption.',
    src: '/media/vikrant-salar/supply-chain-provenance.mp4',
    poster: '/media/vikrant-salar/supply-chain-provenance.webp',
  },
];

const assuranceLoop = [
  { step: '01', title: 'Inspect', detail: 'Observe components and systems', marker: 'bg-cyan-400' },
  { step: '02', title: 'Validate', detail: 'Test integrity and reliability', marker: 'bg-blue-500' },
  { step: '03', title: 'Trace', detail: 'Connect provenance and dependencies', marker: 'bg-violet-500' },
  { step: '04', title: 'Decide', detail: 'Translate evidence into action', marker: 'bg-amber-400' },
];

const experience = [
  {
    icon: Cpu,
    title: 'Semiconductors & hardware',
    text: 'SoC/ASIC platforms, board-level design, component engineering, high-speed interfaces, power, thermal behavior, validation, and system bring-up.',
  },
  {
    icon: Workflow,
    title: 'Technical program leadership',
    text: 'Cross-functional execution from architecture and requirements through NPI, manufacturing readiness, qualification, launch, and lifecycle sustainment.',
  },
  {
    icon: ShieldCheck,
    title: 'Reliability & mission assurance',
    text: 'Evidence-driven engineering for failure analysis, supplier and component risk, provenance, resilient electronics, and decision traceability.',
  },
  {
    icon: Network,
    title: 'AI, infrastructure & supply chains',
    text: 'Research and invention spanning AI infrastructure assurance, semiconductor supply-chain intelligence, trusted hardware, and advanced computing.',
  },
];

const personSchema = {
  '@context': 'https://schema.org',
  '@type': 'Person',
  name: 'Vikrant Salar',
  url: 'https://www.salartechnologies.com/vikrant-salar',
  image: 'https://www.salartechnologies.com/img/vikrant-salar-founder.jpg',
  jobTitle: 'Founder & Principal',
  worksFor: {
    '@type': 'Organization',
    name: 'SALAR Technologies LLC',
    url: 'https://www.salartechnologies.com',
  },
  description: 'Engineer, inventor, author, R&D leader, and technical program executive with 21 years of experience across semiconductors, broadband access, mobile backhaul, hardware systems, reliability, manufacturing, AI infrastructure, and supply-chain resilience. Named inventor across 11 patent filings.',
  knowsAbout: [
    'Semiconductor engineering',
    'SoC and ASIC systems',
    'Board-level hardware design',
    'Electronics reliability',
    'Technical program management',
    'AI infrastructure assurance',
    'Semiconductor supply-chain resilience',
    'Quantum computing',
    'DSLAM and broadband access systems',
    'Mobile backhaul routing',
  ],
  alumniOf: {
    '@type': 'CollegeOrUniversity',
    name: 'Graphic Era Institute of Technology / HNB Garhwal University affiliation',
  },
  sameAs: [
    'https://www.linkedin.com/in/vikrant-salar-76a89416',
    'https://www.linkedin.com/company/112975340/',
    'https://www.amazon.com/dp/B0H743CQQQ',
    'https://www.amazon.com/dp/B0H7F7N5HZ',
  ],
};

const videoSchema = {
  '@context': 'https://schema.org',
  '@graph': videos.map((video) => ({
    '@type': 'VideoObject',
    name: video.title,
    description: video.description,
    thumbnailUrl: `https://www.salartechnologies.com${video.poster}`,
    uploadDate: '2026-08-24',
    duration: 'PT10S',
    contentUrl: `https://www.salartechnologies.com${video.src}`,
  })),
};

export default function VikrantSalar() {
  return (
    <main className="min-h-screen bg-[#071224] text-white">
      <Helmet>
        <title>Vikrant Salar | Founder & Principal, SALAR Technologies LLC</title>
        <meta name="description" content="Vikrant Salar is the Founder & Principal of SALAR Technologies LLC—an engineer, inventor across 11 patent filings, author, R&D leader, and technical program executive with 21 years of experience spanning semiconductors, broadband access, mobile backhaul, hardware reliability, AI infrastructure, and supply-chain resilience." />
        <meta name="author" content="Vikrant Salar" />
        <meta name="robots" content="index, follow, max-image-preview:large" />
        <link rel="canonical" href="https://www.salartechnologies.com/vikrant-salar" />
        <meta property="og:type" content="profile" />
        <meta property="og:title" content="Vikrant Salar | Founder & Principal" />
        <meta property="og:description" content="21 years across semiconductors, broadband access, mobile backhaul, hardware systems, reliability, manufacturing, AI infrastructure, and technical program leadership; inventor across 11 patent filings." />
        <meta property="og:url" content="https://www.salartechnologies.com/vikrant-salar" />
        <meta property="og:image" content="https://www.salartechnologies.com/img/vikrant-salar-founder.jpg" />
        <meta property="og:image:alt" content="Vikrant Salar, Founder & Principal of SALAR Technologies LLC" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Vikrant Salar | Founder & Principal" />
        <meta name="twitter:description" content="Engineer, inventor, author, and technical program leader with 21 years of experience." />
        <meta name="twitter:image" content="https://www.salartechnologies.com/img/vikrant-salar-founder.jpg" />
        <script type="application/ld+json">{JSON.stringify(personSchema)}</script>
        <script type="application/ld+json">{JSON.stringify(videoSchema)}</script>
      </Helmet>

      <section className="relative overflow-hidden border-b border-cyan-400/15">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_78%_20%,rgba(34,211,238,0.16),transparent_35%),linear-gradient(145deg,#071224_0%,#0b2340_55%,#071224_100%)]" />
        <div className="container relative mx-auto grid max-w-7xl grid-cols-1 items-center gap-10 px-6 py-16 lg:grid-cols-[0.78fr_1.22fr] lg:py-24">
          <div className="mx-auto w-full max-w-[430px]">
            <div className="overflow-hidden rounded-2xl border border-cyan-300/25 bg-blue-950/60 p-2 shadow-2xl shadow-cyan-950/40">
              <img
                src="/img/vikrant-salar-founder.jpg"
                alt="Vikrant Salar, Founder and Principal of SALAR Technologies LLC"
                width="1115"
                height="1411"
                fetchpriority="high"
                className="block h-auto w-full rounded-xl object-cover"
              />
            </div>
          </div>
          <div>
            <p className="mb-4 text-xs font-bold uppercase tracking-[0.22em] text-cyan-300">Founder profile</p>
            <h1 className="text-5xl font-black tracking-tight text-white md:text-7xl">Vikrant Salar</h1>
            <p className="mt-3 text-xl font-semibold text-cyan-300 md:text-2xl">Founder &amp; Principal, SALAR Technologies LLC</p>
            <p className="mt-7 max-w-3xl text-lg leading-relaxed text-blue-100 md:text-xl">
              Engineer, inventor, author, and technical program leader with <strong className="text-white">21 years of experience</strong> building and guiding complex semiconductor, electronics, networking, and infrastructure programs—from architecture and board design through validation, manufacturing, reliability, and lifecycle execution.
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              {['21 years engineering', '11 patent filings', '2 published books', 'Founder-led R&D'].map((item) => (
                <span key={item} className="rounded-full border border-cyan-300/30 bg-cyan-300/10 px-4 py-2 text-sm font-semibold text-cyan-100">{item}</span>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section className="border-y border-cyan-400/15 bg-[#0a192d] py-16 md:py-20" aria-labelledby="founder-videos">
        <div className="container mx-auto max-w-7xl px-6">
          <div className="mx-auto max-w-4xl text-center">
            <p className="text-xs font-bold uppercase tracking-[0.22em] text-cyan-300">Founder-led engineering assurance</p>
            <h2 id="founder-videos" className="mt-3 text-3xl font-bold md:text-5xl">Engineering assurance in motion</h2>
            <p className="mx-auto mt-5 max-w-3xl text-lg leading-relaxed text-blue-100">
              Four interconnected perspectives show how SALAR Technologies turns physical evidence, infrastructure telemetry, and supply-chain provenance into practical engineering decisions.
            </p>
          </div>

          <div className="mt-12 grid grid-cols-1 items-center gap-10 rounded-2xl border border-cyan-400/20 bg-[#071224]/80 p-6 shadow-2xl shadow-cyan-950/30 lg:grid-cols-[0.82fr_1.18fr] lg:p-10">
            <div>
              <div
                role="img"
                aria-label="SALAR Engineering Assurance Loop connecting inspection, validation, provenance tracing, and evidence-based decisions"
                className="relative mx-auto flex aspect-square w-full max-w-[320px] items-center justify-center rounded-full bg-[conic-gradient(from_45deg,#22d3ee_0deg_90deg,#3b82f6_90deg_180deg,#8b5cf6_180deg_270deg,#f59e0b_270deg_360deg)] p-7 shadow-[0_0_70px_rgba(34,211,238,0.18)]"
              >
                <div className="flex h-full w-full flex-col items-center justify-center rounded-full border border-white/15 bg-[#071224] px-8 text-center shadow-inner">
                  <span className="text-xs font-bold uppercase tracking-[0.2em] text-cyan-300">SALAR</span>
                  <strong className="mt-2 text-2xl leading-tight text-white">Engineering Assurance</strong>
                  <span className="mt-2 text-sm text-blue-200">A continuous evidence loop</span>
                </div>
              </div>
            </div>

            <div>
              <p className="text-xs font-bold uppercase tracking-[0.2em] text-cyan-300">From evidence to resilient decisions</p>
              <h3 className="mt-3 text-2xl font-bold md:text-3xl">One connected lifecycle, four assurance lenses</h3>
              <p className="mt-4 leading-relaxed text-blue-100">
                The loop connects what engineers observe in the laboratory with what program leaders, investors, procurement teams, and infrastructure operators need to decide.
              </p>
              <ol className="mt-7 grid grid-cols-1 gap-4 sm:grid-cols-2">
                {assuranceLoop.map((item) => (
                  <li key={item.step} className="rounded-xl border border-blue-400/20 bg-blue-950/45 p-4">
                    <div className="flex items-center gap-3">
                      <span className={`h-3 w-3 rounded-full ${item.marker}`} aria-hidden="true" />
                      <span className="text-xs font-bold tracking-widest text-blue-300">{item.step}</span>
                      <strong className="text-white">{item.title}</strong>
                    </div>
                    <p className="mt-2 text-sm leading-relaxed text-blue-200">{item.detail}</p>
                  </li>
                ))}
              </ol>
            </div>
          </div>

          <div className="mt-12 grid grid-cols-1 gap-6 sm:grid-cols-2 xl:grid-cols-4">
            {videos.map((video) => (
              <article key={video.src} className="overflow-hidden rounded-2xl border border-blue-400/25 bg-[#071224] shadow-xl transition duration-300 hover:-translate-y-1 hover:border-cyan-300/55">
                <video
                  controls
                  playsInline
                  preload="metadata"
                  poster={video.poster}
                  aria-label={`${video.title}, ten-second narrated SALAR Technologies video`}
                  className="aspect-[9/16] w-full bg-black object-cover"
                >
                  <source src={video.src} type="video/mp4" />
                  Your browser does not support embedded video.
                </video>
                <div className="p-5">
                  <p className="text-[0.68rem] font-bold uppercase tracking-[0.16em] text-cyan-300">{video.category}</p>
                  <h3 className="mt-2 text-xl font-bold text-white">{video.title}</h3>
                  <p className="mt-3 text-sm leading-relaxed text-blue-200">{video.description}</p>
                  <div className="mt-4 flex items-center gap-2 text-xs font-semibold text-cyan-200">
                    <Volume2 size={14} aria-hidden="true" />
                    <span>10-second narrated video — select play for sound</span>
                  </div>
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section className="py-16 md:py-20">
        <div className="container mx-auto max-w-7xl px-6">
          <div className="grid grid-cols-1 gap-12 lg:grid-cols-[1.08fr_0.92fr]">
            <div>
              <p className="text-xs font-bold uppercase tracking-[0.2em] text-cyan-300">Professional journey</p>
              <h2 className="mt-3 text-3xl font-bold md:text-4xl">From silicon and systems to resilient technology</h2>
              <div className="mt-7 space-y-5 text-lg leading-relaxed text-blue-100">
                <p>
                  Vikrant’s career spans semiconductor and technology environments including Intel, Citrix, Cisco, NetApp, and Nokia / Nokia Siemens Networks (NSN). At Nokia / NSN, he worked on the D50 and D500 DSLAM platforms and Agora mobile-backhaul router programs, contributing to broadband-access and carrier-network hardware. His work has connected deep engineering detail with executive-level program ownership across global teams, suppliers, laboratories, manufacturing partners, and customers.
                </p>
                <p>
                  He has led work involving SoC/ASIC platforms, networking and wireless hardware, board architecture and redesign, component selection, system integration, validation, reliability, root-cause analysis, NPI, supply continuity, manufacturing readiness, and complex cross-functional delivery.
                </p>
                <p>
                  He founded SALAR Technologies LLC to turn fragmented technical evidence into practical engineering decisions—strengthening electronics reliability, trusted hardware, semiconductor supply-chain resilience, AI infrastructure assurance, and mission continuity.
                </p>
              </div>
            </div>
            <aside className="rounded-2xl border border-cyan-400/20 bg-gradient-to-br from-blue-900/45 to-cyan-950/35 p-7 md:p-9">
              <p className="text-xs font-bold uppercase tracking-[0.2em] text-cyan-300">Founder’s vision</p>
              <blockquote className="mt-5 text-2xl font-semibold leading-snug text-white">
                “Build technology that makes complex systems more trustworthy, explainable, resilient, and ready for the missions that depend on them.”
              </blockquote>
              <p className="mt-6 leading-relaxed text-blue-100">
                SALAR combines engineering discipline, research, invention, and evidence-based decision support so organizations can act before hidden hardware, supplier, manufacturing, or infrastructure risk becomes operational failure.
              </p>
            </aside>
          </div>
        </div>
      </section>

      <section className="border-y border-cyan-400/15 bg-[#08172a] py-16">
        <div className="container mx-auto max-w-6xl px-6">
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-cyan-300">Education & engineering foundation</p>
          <h2 className="mt-3 text-3xl font-bold md:text-4xl">Electronics & Communication Engineering</h2>
          <p className="mt-5 max-w-4xl text-lg leading-relaxed text-blue-100">
            Vikrant earned a <strong className="text-white">B.Tech. (Hons.) in Electronics & Communication Engineering in 2006</strong>. Current professional records identify Graphic Era Institute of Technology, then affiliated with HNB Garhwal University, as the degree pathway.
          </p>
          <p className="mt-5 max-w-4xl text-lg leading-relaxed text-blue-100">
            That engineering foundation developed into more than two decades of work across semiconductor systems, networking, hardware reliability, manufacturing, technical program leadership, and AI infrastructure.
          </p>
          <div className="mt-6 flex flex-wrap gap-3">
            <a href="/vikrant-salar-biography.html" className="rounded-lg border border-cyan-400/30 px-5 py-3 font-semibold text-cyan-200 hover:bg-cyan-400/10">Full biography</a>
            <a href="/vikrant-salar-industry-contributions.html" className="rounded-lg border border-blue-400/30 px-5 py-3 font-semibold text-blue-200 hover:bg-blue-400/10">Industry contributions</a>
          </div>
        </div>
      </section>

      <section className="bg-blue-950/30 py-16">
        <div className="container mx-auto max-w-7xl px-6">
          <h2 className="text-center text-3xl font-bold md:text-4xl">Experience that connects the full lifecycle</h2>
          <div className="mt-10 grid grid-cols-1 gap-6 md:grid-cols-2">
            {experience.map(({ icon: Icon, title, text }) => (
              <article key={title} className="rounded-xl border border-blue-400/25 bg-blue-900/25 p-6">
                <Icon className="text-cyan-300" size={32} aria-hidden="true" />
                <h3 className="mt-4 text-xl font-bold">{title}</h3>
                <p className="mt-3 leading-relaxed text-blue-100">{text}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section className="py-16 md:py-20">
        <div className="container mx-auto max-w-6xl px-6">
          <div className="text-center">
            <BookOpen className="mx-auto text-cyan-300" size={38} aria-hidden="true" />
            <h2 className="mt-4 text-3xl font-bold md:text-4xl">Author &amp; inventor</h2>
            <p className="mx-auto mt-4 max-w-3xl text-lg text-blue-100">
              Vikrant is the author of two books and the named inventor across eleven patent filings covering advanced electronics, AI, semiconductor systems, reliability, provenance, and engineering governance.
            </p>
          </div>
          <div className="mt-10 grid grid-cols-1 gap-6 md:grid-cols-2">
            <a href="https://www.amazon.com/dp/B0H743CQQQ" target="_blank" rel="noopener noreferrer" className="group rounded-xl border border-cyan-400/25 bg-blue-900/30 p-7 transition hover:border-cyan-300/70 hover:bg-blue-900/50">
              <p className="text-xs font-bold uppercase tracking-widest text-cyan-300">Book</p>
              <h3 className="mt-3 text-2xl font-bold group-hover:text-cyan-200">The American Arsenal</h3>
              <p className="mt-2 text-blue-100">Defense Electronics Supply-Chain Sovereignty</p>
            </a>
            <a href="https://www.amazon.com/dp/B0H7F7N5HZ" target="_blank" rel="noopener noreferrer" className="group rounded-xl border border-cyan-400/25 bg-blue-900/30 p-7 transition hover:border-cyan-300/70 hover:bg-blue-900/50">
              <p className="text-xs font-bold uppercase tracking-widest text-cyan-300">Book</p>
              <h3 className="mt-3 text-2xl font-bold group-hover:text-cyan-200">Quantum Computing in the Age of Artificial Intelligence</h3>
              <p className="mt-2 text-blue-100">Engineering the Next Civilization</p>
            </a>
          </div>
          <div className="mt-12 text-center">
            <Link to="/contact" className="inline-flex items-center rounded-lg bg-cyan-500 px-6 py-3 font-bold text-[#071224] transition hover:bg-cyan-300">
              Contact SALAR Technologies <ArrowRight className="ml-2" size={19} />
            </Link>
          </div>
        </div>
      </section>
    </main>
  );
}
